import { ComponentRef, Injectable, Injector, Renderer2, RendererFactory2, } from '@angular/core';
import { PieceType } from '../../models/FigureType';
import { IAnatomicMap } from '../../models/IAnatomicMap';
import { IArc } from '../../models/IArc';
import { IPoint } from '../../models/IPoint';
import { ISegmentItem } from '../../models/ISegmentItem';

import { GetPathGeneratorService } from '../pathGenerator.service';
import { SvgShapeService } from '../svgShapeService/svgShapeService';
import { IColorData } from '../../models/IColorData';
import { IClickPoint } from '../../models/IClickPoint';
import { DomService } from '../domService/dom.service';
import { AnatomicMapTooltipComponent, } from '../../components/anatomic-map-tooltip/anatomic-map-tooltip.component';
import { SegmentLabels } from '@se/cp.common';

@Injectable({ providedIn: 'root' })
export class MapDrawerService {
    private renderer: Renderer2;
    private tooltipRef: ComponentRef<AnatomicMapTooltipComponent> | undefined;
    private tooltipCreationTimeOutRef: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private svgService: SvgShapeService,
        private pathGenService: GetPathGeneratorService,
        private tooltipService: DomService,
        private injector: Injector,
        rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public drawMap(
        view: IAnatomicMap,
        colorDatas: IColorData[],
        segmentLabelType: SegmentLabels,
        isWithTooltip: boolean,
        viewBoxXY: IPoint,
    ): HTMLOrSVGElement {
        this.removeTooltipIfRegAndClearTime();
        const mapGroup = this.svgService.drawGroup(view?.viewId!);
        const sergmentsGroup = this.svgService.drawGroup('segments-group');
        const labelsGroup = this.svgService.drawGroup('labels-group');
        let segmentSelected: ISegmentItem = null;
        view.segmentCollection.forEach((segment: ISegmentItem) => {
            if (segment.isVisited) {
                segmentSelected = segment;
            } else {
                this.createSVGSegment(
                    segment,
                    colorDatas,
                    isWithTooltip,
                    mapGroup,
                    sergmentsGroup,
                    labelsGroup,
                    viewBoxXY,
                    segmentLabelType,
                );
            }
        });

        // Appendo il selezionato solo per ultimo
        if (!!segmentSelected) {
            this.createSVGSegment(
                segmentSelected,
                colorDatas,
                isWithTooltip,
                mapGroup,
                sergmentsGroup,
                labelsGroup,
                viewBoxXY,
                segmentLabelType,
                true,
            );
        }
        return mapGroup;
    }

    private createSVGSegment(
        segment: ISegmentItem,
        colorDatas: IColorData[],
        isWithTooltip: boolean,
        mapGroup: SVGGElement,
        sergmentsGroup: SVGGElement,
        labelsGroup: SVGGElement,
        viewBoxXY: IPoint,
        segmentLabelType: SegmentLabels,
        isVisited?: boolean,
    ) {
        const corrispondentData = this.getSegmentData(segment, colorDatas);
        const newSegment = isWithTooltip
            ? this.registerTooltipEmitter(
                  this.registerPointEmitter(this.drawSegment(segment, corrispondentData), segment.regionID),
                  mapGroup,
                  segment,
                  corrispondentData,
                  viewBoxXY,
              )
            : this.registerPointEmitter(this.drawSegment(segment, corrispondentData), segment.regionID);
        this.svgService.appendChild(sergmentsGroup, newSegment);
        if (segmentLabelType !== SegmentLabels.none) {
            this.svgService.appendChild(
                labelsGroup,
                this.svgService.drawSegmentLabel(segment, corrispondentData, segmentLabelType, isVisited),
            );
        }
        this.svgService.appendChild(mapGroup, sergmentsGroup);
        this.svgService.appendChild(mapGroup, labelsGroup);
    }

    private getSegmentData(segment: ISegmentItem, colorDatas: IColorData[]): IColorData {
        const data = colorDatas.find((color) => color.regionID === segment.regionID);
        if (!data) {
            // return colorDatas[0]; // TODO: gestire la trasparenza diversamente
        }
        return data;
    }

    private drawSegment(segment: ISegmentItem, fill: IColorData): HTMLOrSVGElement {
        return this.svgService.drawSegment(segment, fill, this.generatePathD(segment.pieces));
    }

    private generatePathD(pieces: PieceType[]): string {
        const pathStringGenerator = this.pathGenService.getPathGenerator();
        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i],
                isFirst = i === 0 ? true : false;
            if (this.arcTypeCheck(piece)) {
                const nextPiece = i === pieces.length - 1 ? pieces[0] : pieces[i + 1];
                pathStringGenerator.addArc(piece, isFirst, this.getPieceFirstPoint(nextPiece));
            } else {
                pathStringGenerator.addLine(piece, isFirst);
            }
        }
        pathStringGenerator.close();
        return pathStringGenerator.result;
    }

    private arcTypeCheck(i: PieceType): i is IArc {
        return 'radius' in i;
    }

    private getPieceFirstPoint(piece: PieceType): IPoint {
        if (this.arcTypeCheck(piece)) {
            return piece.start;
        }
        return piece.points[0];
    }

    private registerPointEmitter(path: HTMLOrSVGElement, fatherId: string): HTMLOrSVGElement {
        return this.svgService.addListener(path, 'click', (ev) => {
            this.removeTooltipIfRegAndClearTime();
            this.svgService.appendChild(path, this.svgService.drawClickPoint(this.generateClickPoint(fatherId, ev)));
        });
    }
    // private listenedElement:  ()=>void ;

    private registerTooltipEmitter(
        emitter: HTMLOrSVGElement,
        container: HTMLOrSVGElement,
        s: ISegmentItem,
        data: IColorData,
        viewBoxXY: IPoint,
    ): HTMLOrSVGElement {
        this.renderer.listen(emitter, 'mouseenter', (event: any) => {
            this.removeTooltipIfRegAndClearTime();
            event.stopImmediatePropagation();
            this.tooltipCreationTimeOutRef = setTimeout(() => {
                const injector = Injector.create({
                    providers: [
                        {
                            provide: 'tooltipConfig',
                            useValue: {
                                top: event.target.getBoundingClientRect().y,
                                left:
                                    event.target.getBoundingClientRect().x +
                                    event.target.getBoundingClientRect().width / 2,
                            },
                        },
                    ],
                    parent: this.injector,
                });
                const content = this.renderer.createText(`${data?.segmentDescription}\n${data?.description}`);
                this.tooltipRef = this.tooltipService.appendComponentToBody(
                    AnatomicMapTooltipComponent,
                    null,
                    injector,
                    [[content]],
                );
            }, 1000);
        });
        this.renderer.listen(emitter, 'mouseleave', this.removeTooltipIfRegAndClearTime.bind(this));
        return emitter;
    }

    private removeTooltipIfRegAndClearTime() {
        clearTimeout(this.tooltipCreationTimeOutRef!);
        if (this.tooltipRef) {
            this.tooltipService.removeComponentFromBody(this.tooltipRef);
        }
    }

    private generateClickPoint(fatherId: string, event: MouseEvent): IClickPoint {
        return {
            coo: {
                x: event.x,
                y: event.y,
            },
            fatherId,
        };
    }
}
